/* Revenue Opportunities — the AI proposals card, then each open opportunity as a
   plain row: what it is, the recommended action, its value ("—" when unknown) and
   plain buttons. Groups with nothing in them are not shown.

   Colours come from the theme tokens, not from literals.

   This file hardcoded a LIGHT palette — white surfaces, pale-grey borders,
   slate ink — which is the same bug as hardcoding a dark one, just pointing the
   other way: it renders correctly in exactly one theme and wrong in the other.
   The tokens are redeclared under :root[data-theme='light'], so reading them is
   how a surface follows the app's own toggle rather than guessing. */
import React, { useEffect, useMemo, useState } from "react";
import api from "../../apiClient";
import ProposedActionsCard from "../ai/ProposedActionsCard";
import Button from "../ui/button";
import RevivalHeader from "./RevivalHeader";
import { money } from "./revivalFormat";
import "./revival.css";
import "../ai/ProposedActionsCard.css";

const toRows = (value) =>
  Array.isArray(value) ? value : Array.isArray(value?.results) ? value.results : [];

const clean = (value) => String(value || "").trim();

// Unknown is "—", never "$0.00". The API cannot tell the two apart: /opportunities/
// sends float(row.estimated_revenue or 0) and /revenue-insights/ sums with `or 0`
// (analytics_app/views.py), so an absent estimate arrives as 0. On this screen a 0
// estimate is no estimate.
const estimate = (value) => (Number(value) > 0 ? money(value) : "—");

const normalizeType = (value) => {
  const raw = clean(value).toLowerCase();
  if (raw.includes("react")) return "reactivation";
  if (raw.includes("upsell")) return "upsell";
  if (raw.includes("payment")) return "payment_recovery";
  if (raw.includes("service")) return "service_gap";
  return "reactivation";
};

const GROUP_LABEL = {
  reactivation: "Reactivation",
  upsell: "Upsell",
  payment_recovery: "Payment recovery",
  service_gap: "Service gap",
};

const GROUP_ORDER = [
  "reactivation",
  "upsell",
  "payment_recovery",
  "service_gap",
];

export default function AiInsights() {
  const [loading, setLoading] = useState(true);
  const [insights, setInsights] = useState(null);
  const [opportunities, setOpportunities] = useState([]);
  const [runningAction, setRunningAction] = useState("");

  const grouped = useMemo(() => {
    const map = {
      reactivation: [],
      upsell: [],
      payment_recovery: [],
      service_gap: [],
    };

    opportunities.forEach((item) => {
      const key = normalizeType(item.type || item.opportunity_type);
      map[key].push(item);
    });
    return map;
  }, [opportunities]);

  const load = async () => {
    setLoading(true);
    const [insightsData, opportunitiesData] = await Promise.all([
      api
        .get("/revenue-insights/")
        .then((res) => res.data || null)
        .catch(() => null),
      api
        .get("/opportunities/")
        .then((res) => toRows(res.data))
        .catch(() => []),
    ]);
    setInsights(insightsData);
    setOpportunities(opportunitiesData);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const sendSms = async (item) => {
    const customerId = item.customer_id || item.customer?.id;
    if (!customerId || !clean(item.recommended_action)) return;
    setRunningAction(`sms-${item.id}`);
    try {
      await api.post("/comms/send/", {
        customer_id: customerId,
        channel: "sms",
        // The text is the opportunity's own recommended message; with none there
        // is nothing to send (the button is off) — never a canned platform line.
        body: clean(item.recommended_action),
      });
    } finally {
      setRunningAction("");
    }
  };

  const createTask = async (item) => {
    const leadId = item.lead_id || item.lead?.id;
    if (!leadId) return;
    setRunningAction(`task-${item.id}`);
    try {
      await api.post("/tasks/", {
        lead: leadId,
        title: "Revenue opportunity follow-up",
        description: clean(item.recommended_action) || "Review and action this opportunity.",
        task_type: "follow_up",
        status: "pending",
      });
    } finally {
      setRunningAction("");
    }
  };

  const openCustomer = (item) => {
    const customerId = item.customer_id || item.customer?.id || item.customer_external_id;
    if (!customerId) return;
    window.location.href = `/customers/list?search=${encodeURIComponent(String(customerId))}`;
  };

  const scheduleAppointment = () => {
    window.location.href = "/schedule/day";
  };

  const shown = GROUP_ORDER.filter((g) => grouped[g].length);
  const recovery = insights?.estimated_revenue_recovery;

  return (
    <div className="rv-screen">
      <RevivalHeader
        title="Revenue opportunities"
        totals={loading ? [] : [`${opportunities.length} open`, `Estimated recovery ${estimate(recovery)}`]}
      />
      <ProposedActionsCard />
      {loading ? <div className="rv-loading">Loading opportunities…</div> : null}
      {!loading && !shown.length ? (
        <p className="rv-note">No open revenue opportunities. They appear here as the assistant finds them.</p>
      ) : null}
      {!loading && shown.map((group) => (
        <section key={group}>
          <h2 className="rv-section-title">{GROUP_LABEL[group]} <span className="rv-cell-dim">{grouped[group].length}</span></h2>
          <div className="rv-opps">
            {grouped[group].map((item) => (
              <div key={`${group}-${item.id}`} className="rv-opp">
                <div className="rv-opp-main">
                  <b>{clean(item.source) || "Opportunity"}</b>
                  <p>{clean(item.recommended_action) || "No recommended action supplied."}</p>
                  <small>Priority {clean(item.priority_score) || "—"} · {clean(item.status) || "open"}</small>
                </div>
                <div className="rv-opp-end">
                  <b>{estimate(item.estimated_value)}</b>
                  <div className="rv-opp-actions">
                    <Button size="sm" onClick={() => sendSms(item)} loading={runningAction === `sms-${item.id}`}
                      disabled={!item.customer_id || !clean(item.recommended_action)}
                      title={!clean(item.recommended_action) ? "No recommended message to send" : undefined}>
                      Send SMS
                    </Button>
                    <Button size="sm" onClick={() => createTask(item)} loading={runningAction === `task-${item.id}`} disabled={!item.lead_id}>
                      Create task
                    </Button>
                    <Button size="sm" onClick={scheduleAppointment}>Schedule</Button>
                    <Button size="sm" variant="ghost" onClick={() => openCustomer(item)} disabled={!item.customer_id && !item.customer_external_id}>
                      Open customer
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
